import { Injectable, OnDestroy } from "@angular/core";
import { MatPaginatorIntl } from "@angular/material/paginator";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";

/**
 * QSL-10245: dual-language mat-paginator labels. Pulls strings from the `shared`
 * ui_translation scope via ngx-translate and refreshes on language change so
 * every paginator app-wide flips with the UI language (English fallback if a key
 * is missing). Registered globally as MatPaginatorIntl in AppModule.
 */
@Injectable()
export class TranslatePaginatorIntl extends MatPaginatorIntl implements OnDestroy {
  private readonly sub: Subscription;

  constructor(private readonly translate: TranslateService) {
    super();
    this.sub = this.translate.onLangChange.subscribe(() => this.refresh());
    this.refresh();
  }

  private t(key: string, fallback: string): string {
    const v = this.translate.instant(key);
    return v && v !== key ? v : fallback;
  }

  private refresh(): void {
    this.itemsPerPageLabel = this.t("shared_paginator_items_per_page", "Items per page");
    this.nextPageLabel = this.t("shared_paginator_next", "Next page");
    this.previousPageLabel = this.t("shared_paginator_prev", "Previous page");
    this.firstPageLabel = this.t("shared_paginator_first", "First page");
    this.lastPageLabel = this.t("shared_paginator_last", "Last page");
    this.changes.next();
  }

  override getRangeLabel = (page: number, pageSize: number, length: number): string => {
    const of = this.t("shared_paginator_of", "of");
    if (length === 0 || pageSize === 0) return `0 ${of} ${length}`;
    const start = page * pageSize;
    const end = Math.min(start + pageSize, length);
    return `${start + 1} – ${end} ${of} ${length}`;
  };

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }
}
